import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { WalkInsService } from '../../../services/business/walk-ins.service';
import { WalkInBoard, WalkInProfessional, WalkInTicket } from '../../../shared/interfaces';
import { PageTitleService } from '../../../shared/services';

const REFRESH_INTERVAL_MS = 30_000;

/**
 * Tablero de turnos por orden de llegada, solo lectura.
 * La gestión (registrar, asignar, terminar) se hace desde la app móvil.
 */
@Component({
  selector: 'app-walk-ins',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './walk-ins.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalkIns implements OnInit, OnDestroy {
  private readonly walkInsService = inject(WalkInsService);
  private readonly titleService = inject(PageTitleService);

  board = signal<WalkInBoard | null>(null);
  isLoading = signal(true);
  loadError = signal(false);
  lastUpdated = signal<Date | null>(null);

  freeCount = computed(() => this.board()?.professionals.filter((p) => p.state === 'free').length ?? 0);
  busyCount = computed(() => this.board()?.professionals.filter((p) => p.state === 'busy').length ?? 0);
  longestWait = computed(() => {
    const queue = this.board()?.queue ?? [];
    return queue.length ? Math.max(...queue.map((t) => t.waitMinutes)) : 0;
  });

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Turnos',
      subtitle: 'Quién está libre y quién espera, en tiempo real',
      breadcrumbs: [{ name: 'Calendario', url: '/agendas' }],
    });
    this.load();
    this.refreshTimer = setInterval(() => this.load(true), REFRESH_INTERVAL_MS);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  load(silent = false): void {
    if (!silent) this.isLoading.set(true);

    this.walkInsService.getBoard().subscribe({
      next: (board) => {
        this.board.set(board);
        this.loadError.set(false);
        this.isLoading.set(false);
        this.lastUpdated.set(new Date());
      },
      error: () => {
        // A failed background refresh keeps the last board on screen
        this.loadError.set(!this.board());
        this.isLoading.set(false);
      },
    });
  }

  // ─── Display helpers ───

  clock(value: string | Date | null): string {
    if (!value) return '';
    return new Date(value).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' });
  }

  minutesUntil(value: string | null): number {
    if (!value) return 0;
    return Math.ceil((new Date(value).getTime() - Date.now()) / 60_000);
  }

  initials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  }

  stateLabel(professional: WalkInProfessional): string {
    switch (professional.state) {
      case 'free': return 'Libre';
      case 'busy': return 'Atendiendo';
      case 'blocked': return 'Bloqueado';
      default: return 'Fuera de horario';
    }
  }

  stateClasses(professional: WalkInProfessional): string {
    switch (professional.state) {
      case 'free': return 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300';
      case 'busy': return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      default: return 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300';
    }
  }

  dotClasses(professional: WalkInProfessional): string {
    switch (professional.state) {
      case 'free': return 'bg-green-500';
      case 'busy': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  }

  countdown(professional: WalkInProfessional): string | null {
    if (!professional.until || professional.state === 'closed') return null;
    const minutes = this.minutesUntil(professional.until);
    if (minutes <= 0 || minutes >= 180) return null;
    return minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
  }

  stateDetail(professional: WalkInProfessional): string {
    const until = professional.until ? this.clock(professional.until) : null;
    switch (professional.state) {
      case 'free': {
        if (professional.nextEvent && until) return `Libre hasta su cita de las ${until}`;
        const endOfDay = professional.until && new Date(professional.until).getHours() === 0 && new Date(professional.until).getMinutes() === 0;
        return until && !endOfDay ? `Libre hasta el cierre (${until})` : 'Libre el resto del día';
      }
      case 'busy': {
        const current = professional.currentEvent;
        const who = current ? `${current.clientName || 'Cliente'} · ${current.serviceName}` : 'En una cita';
        return until ? `${who} · termina ${until}` : who;
      }
      case 'blocked':
        return until ? `Bloqueado hasta las ${until}` : 'Bloqueado';
      default:
        return until ? `Abre a las ${until}` : 'Hoy ya no atiende';
    }
  }

  waitClasses(ticket: WalkInTicket): string {
    if (ticket.waitMinutes >= 30) return 'text-red-600 dark:text-red-400 font-semibold';
    if (ticket.waitMinutes >= 15) return 'text-amber-600 dark:text-amber-400 font-semibold';
    return 'text-gray-500 dark:text-gray-400';
  }

  waitLabel(ticket: WalkInTicket): string {
    const wait = ticket.waitMinutes;
    if (wait < 1) return 'acaba de llegar';
    return wait >= 60 ? `espera ${Math.floor(wait / 60)} h ${wait % 60} min` : `espera ${wait} min`;
  }

  estimateLabel(ticket: WalkInTicket): string {
    if (!ticket.estimatedStart || !ticket.estimatedProfessionalName) return 'Sin espacio hoy';
    const firstName = ticket.estimatedProfessionalName.split(' ')[0];
    return this.minutesUntil(ticket.estimatedStart) <= 1
      ? `Ya con ${firstName}`
      : `~${this.clock(ticket.estimatedStart)} con ${firstName}`;
  }

  isReadyNow(ticket: WalkInTicket): boolean {
    return !!ticket.estimatedStart && this.minutesUntil(ticket.estimatedStart) <= 1;
  }
}
